import * as React from "react";
import { Link } from "@inertiajs/react";
import { ChevronsUpDown, Check, Plus, FolderKanban, Lock } from "lucide-react";
import { Menu } from "@ui/Menu";
import type { Organization, Project } from "@lib/inertia-types";
import { cn } from "@lib/cn";

// The project switcher used to live at the top of the sidebar; with the thin
// rail it moves into the main panel's top bar.
export function ProjectSwitcher({
  currentOrg,
  currentProject,
  projects,
}: {
  currentOrg: Organization | null;
  currentProject: Project | null;
  projects: Project[];
}) {
  if (!currentOrg) {
    return (
      <div className="flex items-center gap-2 rounded-md bg-cloverhound-gray-100 px-1.5 py-1 shadow-sm dark:bg-cloverhound-gray-850">
        <GlyphTile label="Fetch" />
        <span className="truncate text-small font-semibold text-cloverhound-gray-900 dark:text-cloverhound-gray-50">
          Fetch
        </span>
      </div>
    );
  }

  return (
    <Menu.Root>
      <Menu.Trigger
        className={cn(
          // Narrower cap on mobile so the name yields room for breadcrumbs;
          // unchanged (16rem) at md+.
          "flex max-w-[8.5rem] items-center gap-2 rounded-md px-1.5 py-1 shadow-sm md:max-w-[16rem]",
          "bg-cloverhound-gray-100 hover:bg-cloverhound-gray-200 dark:bg-cloverhound-gray-850 dark:hover:bg-cloverhound-gray-800",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cloverhound-indigo-500",
        )}
        aria-label="Project menu"
      >
        <GlyphTile label={currentProject?.name ?? currentOrg.name} />
        <span className="min-w-0 flex-1 truncate text-left text-small font-semibold text-cloverhound-gray-900 dark:text-cloverhound-gray-50">
          {currentProject?.name ?? currentOrg.name}
        </span>
        <ChevronsUpDown
          className="size-3.5 shrink-0 text-cloverhound-gray-600"
          aria-hidden="true"
        />
      </Menu.Trigger>
      <Menu.Content align="start" sideOffset={4}>
        <Menu.Label>Projects in {currentOrg.name}</Menu.Label>
        {projects.length === 0 && (
          <div className="px-2 py-1.5 text-mini text-cloverhound-gray-600">
            No projects yet
          </div>
        )}
        {projects.map((p) => {
          const isCurrent = currentProject?.id === p.id;
          if (p.accessible === false) {
            return (
              <Menu.Item
                key={p.id}
                disabled
                className="opacity-60"
                title="Ask an admin for access"
              >
                <GlyphTile label={p.name} size="sm" />
                <span className="truncate text-cloverhound-gray-500">
                  {p.name}
                </span>
                <Lock
                  className="ml-auto size-3.5 text-cloverhound-gray-500"
                  aria-hidden="true"
                />
              </Menu.Item>
            );
          }
          return (
            <Menu.Item key={p.id} render={<Link href={`/projects/${p.slug}`} />}>
              <GlyphTile label={p.name} size="sm" />
              <span className="truncate">{p.name}</span>
              {isCurrent && (
                <Check
                  className="ml-auto size-3.5 text-cloverhound-gray-700 dark:text-cloverhound-gray-300"
                  aria-hidden="true"
                />
              )}
            </Menu.Item>
          );
        })}
        <Menu.Separator />
        <Menu.Item render={<Link href="/projects" />}>
          <FolderKanban className="size-3.5" aria-hidden="true" />
          All projects
        </Menu.Item>
        <Menu.Item render={<Link href="/projects?new=1" />}>
          <Plus className="size-3.5" aria-hidden="true" />
          New project
        </Menu.Item>
      </Menu.Content>
    </Menu.Root>
  );
}

export function GlyphTile({
  label,
  size = "md",
}: {
  label: string | null | undefined;
  size?: "sm" | "md";
}) {
  const sizeClass =
    size === "sm"
      ? "size-5 text-[10px] rounded-xs"
      : "size-6 text-mini rounded-sm";
  return (
    <div
      className={cn(
        "flex shrink-0 items-center justify-center bg-cloverhound-indigo-600 font-bold text-white",
        sizeClass,
      )}
      aria-hidden="true"
    >
      {label?.[0]?.toUpperCase() ?? "F"}
    </div>
  );
}
